import {
  type JSX,
  type MouseEvent as ReactMouseEvent,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import { Divider, Flex } from '@dynatrace/strato-components/layouts';
import {
  ChevronRightDoubleIcon,
  DownloadIcon,
  DragAllDirectionIcon,
} from '@dynatrace/strato-icons';

import {
  chartToolbar,
  chartToolbarArea,
  grip,
  outerContainer,
  styledHorizontalDivider,
  styledVerticalDivider,
} from './ChartToolbarRenderer.sty.js';
import { CollapseButton } from './CollapseButton.js';
import { ChartDataMenu } from './components/ChartDataMenu.js';
import { Control } from './components/Control.js';
import { Intent } from './components/Intent.js';
import { ToolbarMenu } from './components/ToolbarMenu.js';
import { TOOLBAR_DRAG_BUTTON_ARIA_LABEL } from './constants.js';
import { ChartToolbarContext } from './context/ChartToolbar.context.js';
import { useToolbar } from './hooks/useToolbar.js';
import { TOOLBAR_MESSAGES } from './messages.js';
import { doesFitInBounds } from './utils/does-fit-in-bounds.js';
import { estimateToolbarWidth } from './utils/estimate-toolbar-width.js';
import { getToolbarPlacement } from './utils/get-toolbar-placement.js';
import { useMergeRefs } from '../../../../core/hooks/useMergeRefs.js';
import type { IntentProps } from '../../../../core/slots/Intent/Intent.js';
import { ItemIcon } from '../../../../navigation/menu/ItemIcon.js';
import { Menu } from '../../../../navigation/menu/Menu.js';
import { Tooltip } from '../../../../overlays/tooltip/Tooltip.js';
import { A11yToolbarTabIndexContext } from '../../context/A11yToolbarTabIndexContext.js';
import { useA11yToolbarControls } from '../../hooks/useA11yToolbarControls.js';
import { useDragElement } from '../../hooks/useDragElement.js';
import { useObserveContainerSize } from '../../hooks/useObserveContainerSize.js';
import {
  A11yToolbarTabIndex,
  InternalChartToolbarConfig,
} from '../../types/toolbar.js';
import { getDraggableBounds } from '../../utils/get-draggable-bounds.js';
import { INTERACTION_MODE } from '../interactions-overlay/types.js';

/**
 * @internal
 */
export const menuDisplayModes = [
  'collapsed',
  'expanded',
  'always',
  'never',
] as const;

/**
 * @internal
 */
export type MenuDisplayMode = (typeof menuDisplayModes)[number];

/**
 * @internal
 */
export interface ChartToolbarRendererProps extends InternalChartToolbarConfig {
  /** Whether the toolbar is visible or not */
  visible: boolean;
  /** Initial state of the A11yToolbar */
  initialA11yState: A11yToolbarTabIndex[];
  /** zIndex for the chart toolbar area */
  zIndex?: number;
  /** The function called when the toolbar manually collapses/expands */
  onToggleToolbar?: (value: boolean) => void;
  /** The function called when the menu opens/closes */
  onToggleMenu?: (value: boolean) => void;
  /** The function called when mouse enters the toolbar */
  onMouseEnter?: (event?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
  /** The function called when mouse leaves the toolbar */
  onMouseLeave?: (event?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
  /** Icon of the active mode shown in the menu trigger button */
  menuIcon?: JSX.Element;
  /** Defines a tooltip text for the dropdown menu trigger button */
  menuTriggerLabel?: string;
  /** Indicates, when the menu should be available to be triggered on the toolbar
   * - collapsed: only shown on menu trigger on collapsed toolbar (no menu on expanded toolbar)
   * - expanded: only shown on menu trigger on expanded toolbar (toolbar becomes not collapsable)
   * - always: always show the menu trigger
   * - never: never show the menu trigger
   *
   * @defaultValue 'collapsed'
   * */
  menuDisplayMode?: MenuDisplayMode;
  /** Action to perform while closing toolbar menu */
  onClose?: () => void;
  isDownloadEnabled?: boolean;
  intents?: Array<IntentProps>;
  handleDownloadDataToolbar?: () => void;
  DownloadButton?: () => JSX.Element;
}

/**
 * @internal
 */
export const InternalChartToolbarRenderer = (
  props: PropsWithChildren<ChartToolbarRendererProps>,
) => {
  const {
    children,
    visible,
    placement,
    collapsed = false,
    draggable = true,
    initialA11yState,
    zIndex,
    onToggleToolbar,
    onToggleMenu,
    onMouseEnter,
    onMouseLeave,
    menuIcon,
    menuTriggerLabel,
    menuDisplayMode = 'collapsed',
    onClose,
    isDownloadEnabled,
    intents,
    handleDownloadDataToolbar,
    DownloadButton,
  } = props;

  const {
    isExpanded,
    lastItemSelected,
    updateLastItemSelected,
    toggleExpanded,
    isCollapsable,
    fitsBounds,
    updateFitsBounds,
    shouldShowMenu,
  } = useToolbar(collapsed, menuDisplayMode);

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [toolbarElement, setToolbarElement] = useState<HTMLDivElement>();

  const [boundSize, boundRef] = useObserveContainerSize();
  const [toolbarSize, toolbarRefCallback] = useObserveContainerSize();
  const intl = useIntl();
  const mouseInBounds = useRef(false);

  const toolbarElementRef = useCallback((toolbarElement: HTMLDivElement) => {
    setToolbarElement(toolbarElement);
  }, []);

  const toolbarRef = useMergeRefs([toolbarRefCallback, toolbarElementRef]);

  const { a11yControlsValue, setKeyEvent, setActiveControl } =
    useA11yToolbarControls(initialA11yState);

  const maxBounds = useMemo(
    () => getDraggableBounds(toolbarSize, boundSize),
    [toolbarSize, boundSize],
  );

  useEffect(() => {
    updateFitsBounds(
      doesFitInBounds(estimateToolbarWidth(children), boundSize),
    );
  }, [children, boundSize]);

  useEffect(() => {
    toggleExpanded(fitsBounds && !collapsed);
  }, [collapsed, fitsBounds]);

  const initialPosition = getToolbarPlacement(placement, maxBounds);

  const { isDragging, triggerDragRef } = useDragElement(
    toolbarElement,
    maxBounds,
    initialPosition,
  );

  const expandHandler = useCallback(
    (isExpanded: boolean) => {
      toggleExpanded(isExpanded);
      onToggleToolbar?.(isExpanded);
    },
    [onToggleToolbar],
  );

  const handleToggleMenu = (open: boolean) => {
    if (!open) {
      if (!mouseInBounds.current) {
        onMouseLeave?.();
      } else {
        onMouseEnter?.();
      }
    }

    setIsMenuOpen(open);
    onToggleMenu?.(open);
  };

  const collapseButton = useMemo(
    () =>
      Array.isArray(children) && (
        <CollapseButton
          onClick={(isExpanded: boolean) => {
            expandHandler(isExpanded);
            setActiveControl('explore');
          }}
        />
      ),
    [children, expandHandler, setActiveControl],
  );

  const expandButton = useMemo(
    () => (
      <Control
        prefixIcon={<ChevronRightDoubleIcon />}
        tooltipText={intl.formatMessage(TOOLBAR_MESSAGES.chartToolbarExpand)}
        onClick={() => expandHandler(true)}
        tabIndex={-1}
        testId="toolbar-expand-button"
      />
    ),
    [expandHandler],
  );

  const onMouseEnterHandler = () => {
    mouseInBounds.current = true;
    onMouseEnter?.();
  };

  const onMouseLeaveHandler = (event: ReactMouseEvent) => {
    event.stopPropagation();
    mouseInBounds.current = false;
    onMouseLeave?.();
  };

  const handleOnMenuEnter = () => {
    mouseInBounds.current = false;
  };

  const handleOnMenuLeave = () => {
    mouseInBounds.current = true;
  };

  const onCloseAutoFocus = (e: Event) => {
    if (
      ((lastItemSelected === INTERACTION_MODE.Explore && onClose) ||
        lastItemSelected === INTERACTION_MODE.Zoom) &&
      onClose
    ) {
      // Prevent the autofocus from focusing back on the menu trigger after closing the menu
      e.preventDefault();
      onClose();
      updateLastItemSelected(undefined);
    }
  };

  const showCollapsedIntentSubmenu = (!isExpanded || !fitsBounds) && !!intents;
  const showExpandButton = !isExpanded && fitsBounds;
  const showCollapseButton = isExpanded && isCollapsable;
  const showIntentMenu = isExpanded && fitsBounds && !!intents;

  return (
    <div
      className={chartToolbarArea}
      style={{ zIndex }}
      data-testid="chart-plot-overlay-toolbar"
      ref={boundRef}
    >
      <div
        className={chartToolbar}
        id="chart-toolbar"
        ref={toolbarRef}
        role="toolbar"
        data-testid="chart-toolbar-container"
        onBlur={(event) => {
          event.stopPropagation();
        }}
        onKeyDown={(event) => {
          if (event.code === 'ArrowLeft' || event.code === 'ArrowRight') {
            event.stopPropagation();
            setKeyEvent(event);
          }

          if (event.code === 'Enter') {
            event.stopPropagation();
          }
        }}
        onMouseEnter={onMouseEnterHandler}
        onMouseLeave={onMouseLeaveHandler}
      >
        <A11yToolbarTabIndexContext.Provider value={a11yControlsValue}>
          <div
            className={outerContainer({
              visible: visible || isMenuOpen || isDragging,
            })}
            data-testid="chart-toolbar-wrapper"
          >
            <Flex gap={0} flexDirection="row" alignItems={'center'}>
              {draggable && (
                <>
                  <Tooltip
                    text={intl.formatMessage(TOOLBAR_MESSAGES.chartToolbarMove)}
                    disabled={isDragging}
                  >
                    <Button
                      className={grip}
                      ref={triggerDragRef}
                      variant="default"
                      size={'condensed'}
                      data-testid="chart-toolbar-grip"
                      tabIndex={-1}
                      aria-label={TOOLBAR_DRAG_BUTTON_ARIA_LABEL}
                    >
                      <Button.Prefix>{<DragAllDirectionIcon />}</Button.Prefix>
                    </Button>
                  </Tooltip>
                  <Divider
                    className={styledVerticalDivider}
                    orientation="vertical"
                    as="hr"
                  />
                </>
              )}
              <div
                data-testid="toolbar-children"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                {shouldShowMenu && (
                  <ChartToolbarContext.Provider
                    value={{
                      isMenuElement: true,
                      isExpanded,
                      lastItemSelected,
                      updateLastItemSelected,
                    }}
                  >
                    <ToolbarMenu
                      onMenuOpen={handleToggleMenu}
                      activeModeIcon={menuIcon}
                      triggerLabel={menuTriggerLabel}
                      onMenuEnter={handleOnMenuEnter}
                      onMenuLeave={handleOnMenuLeave}
                      menuAlignment={isExpanded ? 'start' : 'end'}
                      onCloseAutoFocus={onCloseAutoFocus}
                    >
                      {children}
                      {showCollapsedIntentSubmenu ? (
                        <>
                          <Divider
                            className={styledHorizontalDivider}
                            orientation="horizontal"
                            as="hr"
                          />
                          <Menu.Sub>
                            <Menu.SubTrigger>More options</Menu.SubTrigger>
                            <Menu.SubContent>
                              {isDownloadEnabled &&
                                DownloadButton &&
                                handleDownloadDataToolbar && (
                                  <>
                                    <Menu.Item
                                      onSelect={handleDownloadDataToolbar}
                                    >
                                      <ItemIcon>
                                        <DownloadIcon />
                                      </ItemIcon>
                                      Download as CSV
                                    </Menu.Item>

                                    <Divider
                                      className={styledHorizontalDivider}
                                      orientation="horizontal"
                                      as="hr"
                                      style={{ width: 'inherit' }}
                                    />
                                  </>
                                )}
                              {(intents ?? []).map((item, key) => (
                                <Intent {...item} key={key} />
                              ))}
                            </Menu.SubContent>
                          </Menu.Sub>
                        </>
                      ) : null}
                      {showExpandButton && (
                        <>
                          <Divider
                            className={styledHorizontalDivider}
                            orientation="horizontal"
                            as="hr"
                          />
                          {expandButton}
                        </>
                      )}
                    </ToolbarMenu>
                  </ChartToolbarContext.Provider>
                )}
                <ChartToolbarContext.Provider
                  value={{
                    isMenuElement: false,
                    isExpanded,
                    lastItemSelected,
                    updateLastItemSelected,
                  }}
                >
                  {children}
                  {showIntentMenu ? (
                    <ChartDataMenu
                      onMenuOpen={handleToggleMenu}
                      onMenuEnter={handleOnMenuEnter}
                      onMenuLeave={handleOnMenuLeave}
                      isDownloadEnabled={isDownloadEnabled}
                      handleDownloadDataToolbar={handleDownloadDataToolbar}
                      intents={intents}
                      showDivider={!!menuIcon}
                    />
                  ) : null}
                </ChartToolbarContext.Provider>

                {showCollapseButton && collapseButton}
              </div>
            </Flex>
          </div>
        </A11yToolbarTabIndexContext.Provider>
      </div>
    </div>
  );
};

InternalChartToolbarRenderer['displayName'] = 'ChartToolbarRenderer';

/**
 * @internal
 */
export const ChartToolbarRenderer = Object.assign(
  InternalChartToolbarRenderer,
  {
    Control,
  },
);
